import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import { getSEOConfig, updateSEOTags } from '../../i18n/utils/seo';
import type { SupportedLanguage } from '../../i18n/utils/language';

export default function SQLTimestamp() {
  const { t, i18n } = useTranslation(['programming', 'common']);
  const [copySuccess, setCopySuccess] = useState('');
  const [activeDb, setActiveDb] = useState<'mysql' | 'postgresql' | 'oracle' | 'sqlserver'>('mysql');

  // Update SEO
  useEffect(() => {
    const currentLang = i18n.language as SupportedLanguage;
    const seoConfig = getSEOConfig('sqlTimestamp', currentLang);
    updateSEOTags(seoConfig, currentLang);
    document.documentElement.lang = currentLang;
  }, [i18n.language]);

  // Copy to clipboard
  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(id);
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  const CodeBlock = ({ code, language = 'sql', id }: { code: string; language?: string; id: string }) => (
    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
      <button
        onClick={() => copyToClipboard(code, id)}
        className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
      >
        {copySuccess === id ? t('common:button.copied') : t('common:button.copy')}
      </button>
      <pre className="text-green-400 text-sm font-mono whitespace-pre overflow-x-auto">
        {code}
      </pre>
    </div>
  );

  // Database tab component
  const DbTab = ({ db, label }: { db: typeof activeDb; label: string }) => (
    <button
      onClick={() => setActiveDb(db)}
      className={`px-6 py-3 font-semibold rounded-lg transition-all ${
        activeDb === db
          ? 'bg-blue-600 text-white shadow-lg'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-6xl mx-auto px-4 py-4 md:py-8">
        {/* Page Header */}
        <div className="text-center mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            {t('sql.mainTitleSEO')}
          </h1>
          <p className="text-lg text-gray-600">{t('sql.subtitle')}</p>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <p className="text-gray-700 leading-relaxed">{t('sql.intro')}</p>
        </div>

        {/* Database Selector */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl shadow-lg p-6 mb-6 md:mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">{t('sql.selectDatabase')}</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <DbTab db="mysql" label="MySQL / MariaDB" />
            <DbTab db="postgresql" label="PostgreSQL" />
            <DbTab db="oracle" label="Oracle" />
            <DbTab db="sqlserver" label="SQL Server" />
          </div>
        </div>

        {/* MySQL Content */}
        {activeDb === 'mysql' && (
          <>
            {/* UNIX_TIMESTAMP() Function */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">MySQL: UNIX_TIMESTAMP() Function</h2>
              <p className="text-gray-600 mb-6">
                Convert datetime to Unix timestamp (seconds since 1970-01-01 00:00:00 UTC)
              </p>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">1</span>
                    Get Current Unix Timestamp
                  </h3>
                  <CodeBlock
                    id="mysql-1"
                    code={`-- Get current timestamp in seconds
SELECT UNIX_TIMESTAMP();
-- Output: 1735948800

-- Get milliseconds
SELECT UNIX_TIMESTAMP() * 1000;
-- Output: 1735948800000

-- Get microseconds (MySQL 5.6.4+)
SELECT UNIX_TIMESTAMP(NOW(6)) * 1000000;`}
                  />
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">2</span>
                    Convert Date to Timestamp
                  </h3>
                  <CodeBlock
                    id="mysql-2"
                    code={`-- Convert specific datetime
SELECT UNIX_TIMESTAMP('2025-01-04 12:00:00');
-- Output: 1735948800

-- Convert column value
SELECT
  id,
  created_at,
  UNIX_TIMESTAMP(created_at) AS timestamp
FROM users;`}
                  />
                </div>
              </div>
            </div>

            {/* FROM_UNIXTIME() Function */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">MySQL: FROM_UNIXTIME() Function</h2>
              <p className="text-gray-600 mb-6">
                Convert Unix timestamp to MySQL datetime format
              </p>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">1</span>
                    Convert Timestamp to Datetime
                  </h3>
                  <CodeBlock
                    id="mysql-3"
                    code={`SELECT FROM_UNIXTIME(1735948800);
-- Output: 2025-01-04 12:00:00

-- From milliseconds
SELECT FROM_UNIXTIME(1735948800000 / 1000);
-- Output: 2025-01-04 12:00:00`}
                  />
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">2</span>
                    Format with Custom Pattern
                  </h3>
                  <CodeBlock
                    id="mysql-4"
                    code={`SELECT FROM_UNIXTIME(1735948800, '%Y-%m-%d %H:%i:%s');
-- Output: 2025-01-04 12:00:00

SELECT FROM_UNIXTIME(1735948800, '%M %d, %Y at %h:%i %p');
-- Output: January 04, 2025 at 12:00 PM

SELECT FROM_UNIXTIME(1735948800, '%W, %M %d, %Y');
-- Output: Saturday, January 04, 2025`}
                  />
                </div>
              </div>
            </div>

            {/* TIMESTAMPDIFF */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">MySQL: TIMESTAMPDIFF() - Calculate Differences</h2>
              <CodeBlock
                id="mysql-diff"
                code={`-- Difference in seconds
SELECT TIMESTAMPDIFF(SECOND, '2025-01-01 10:00:00', '2025-01-01 10:05:30');
-- Output: 330

-- Difference in days
SELECT TIMESTAMPDIFF(DAY, '2025-01-01', '2025-01-15');
-- Output: 14

-- User age in years
SELECT
  username,
  TIMESTAMPDIFF(YEAR, birth_date, CURDATE()) AS age
FROM users;

-- Posts created in last 24 hours
SELECT title, TIMESTAMPDIFF(HOUR, created_at, NOW()) AS hours_ago
FROM posts
WHERE TIMESTAMPDIFF(HOUR, created_at, NOW()) < 24;`}
              />
            </div>
          </>
        )}

        {/* PostgreSQL Content */}
        {activeDb === 'postgresql' && (
          <>
            {/* Extract Epoch */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">PostgreSQL: EXTRACT(EPOCH FROM timestamp)</h2>
              <p className="text-gray-600 mb-6">
                PostgreSQL uses EXTRACT(EPOCH) to get Unix timestamp from datetime
              </p>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">1</span>
                    Get Current Unix Timestamp
                  </h3>
                  <CodeBlock
                    id="pg-1"
                    code={`-- Get current timestamp (seconds with decimal)
SELECT EXTRACT(EPOCH FROM NOW());
-- Output: 1735948800.123456

-- Get integer timestamp (seconds only)
SELECT FLOOR(EXTRACT(EPOCH FROM NOW()));
-- Output: 1735948800

-- Get milliseconds
SELECT (EXTRACT(EPOCH FROM NOW()) * 1000)::BIGINT;
-- Output: 1735948800123`}
                  />
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">2</span>
                    Convert Date to Timestamp
                  </h3>
                  <CodeBlock
                    id="pg-2"
                    code={`-- Convert specific datetime
SELECT EXTRACT(EPOCH FROM TIMESTAMP '2025-01-04 12:00:00');
-- Output: 1735948800

-- Convert column value
SELECT
  id,
  created_at,
  EXTRACT(EPOCH FROM created_at)::BIGINT AS timestamp
FROM users;`}
                  />
                </div>
              </div>
            </div>

            {/* TO_TIMESTAMP */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">PostgreSQL: TO_TIMESTAMP() Function</h2>
              <p className="text-gray-600 mb-6">
                Convert Unix timestamp to PostgreSQL timestamp
              </p>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">1</span>
                    Convert Timestamp to Datetime
                  </h3>
                  <CodeBlock
                    id="pg-3"
                    code={`-- From seconds
SELECT TO_TIMESTAMP(1735948800);
-- Output: 2025-01-04 12:00:00+00

-- From milliseconds
SELECT TO_TIMESTAMP(1735948800000 / 1000.0);
-- Output: 2025-01-04 12:00:00+00

-- Format as string
SELECT TO_CHAR(TO_TIMESTAMP(1735948800), 'YYYY-MM-DD HH24:MI:SS');
-- Output: 2025-01-04 12:00:00`}
                  />
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">2</span>
                    Custom Formatting
                  </h3>
                  <CodeBlock
                    id="pg-4"
                    code={`SELECT TO_CHAR(TO_TIMESTAMP(1735948800), 'Mon DD, YYYY at HH12:MI AM');
-- Output: Jan 04, 2025 at 12:00 PM

SELECT TO_CHAR(TO_TIMESTAMP(1735948800), 'Day, Month DD, YYYY');
-- Output: Saturday, January 04, 2025

SELECT TO_CHAR(TO_TIMESTAMP(1735948800), 'YYYY/MM/DD HH24:MI:SS');
-- Output: 2025/01/04 12:00:00`}
                  />
                </div>
              </div>
            </div>

            {/* AGE and Date Arithmetic */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">PostgreSQL: Date Arithmetic & AGE()</h2>
              <CodeBlock
                id="pg-age"
                code={`-- Calculate age
SELECT AGE(TIMESTAMP '2025-01-04', TIMESTAMP '2020-01-01');
-- Output: 5 years 3 days

-- Time difference in specific units
SELECT
  EXTRACT(EPOCH FROM (NOW() - created_at)) AS seconds_diff,
  EXTRACT(EPOCH FROM (NOW() - created_at)) / 3600 AS hours_diff,
  EXTRACT(EPOCH FROM (NOW() - created_at)) / 86400 AS days_diff
FROM posts;

-- Add/subtract intervals
SELECT
  NOW() + INTERVAL '7 days' AS one_week_later,
  NOW() - INTERVAL '1 hour' AS one_hour_ago,
  TO_TIMESTAMP(1735948800) + INTERVAL '30 days' AS future_date;`}
              />
            </div>
          </>
        )}

        {/* Oracle Content */}
        {activeDb === 'oracle' && (
          <>
            {/* Oracle SYSTIMESTAMP and conversion */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Oracle: Timestamp Functions</h2>
              <p className="text-gray-600 mb-6">
                Oracle uses different syntax for timestamp operations
              </p>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">1</span>
                    Get Current Unix Timestamp
                  </h3>
                  <CodeBlock
                    id="oracle-1"
                    code={`-- Get current Unix timestamp (seconds)
SELECT (SYSDATE - TO_DATE('1970-01-01', 'YYYY-MM-DD')) * 86400
FROM DUAL;
-- Output: 1735948800

-- Using SYSTIMESTAMP (more precise)
SELECT (CAST(SYSTIMESTAMP AS DATE) - TO_DATE('1970-01-01', 'YYYY-MM-DD')) * 86400
FROM DUAL;

-- Get milliseconds
SELECT (CAST(SYSTIMESTAMP AS DATE) - TO_DATE('1970-01-01', 'YYYY-MM-DD')) * 86400000
FROM DUAL;`}
                  />
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">2</span>
                    Convert Date to Unix Timestamp
                  </h3>
                  <CodeBlock
                    id="oracle-2"
                    code={`-- Convert specific date to timestamp
SELECT (TO_DATE('2025-01-04 12:00:00', 'YYYY-MM-DD HH24:MI:SS')
        - TO_DATE('1970-01-01', 'YYYY-MM-DD')) * 86400
FROM DUAL;
-- Output: 1735948800

-- Convert column to timestamp
SELECT
  id,
  created_date,
  (created_date - TO_DATE('1970-01-01', 'YYYY-MM-DD')) * 86400 AS unix_timestamp
FROM users;`}
                  />
                </div>
              </div>
            </div>

            {/* Convert Unix Timestamp to Date */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Oracle: Convert Unix Timestamp to Date</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">1</span>
                    Convert Timestamp to Date
                  </h3>
                  <CodeBlock
                    id="oracle-3"
                    code={`-- From seconds
SELECT TO_DATE('1970-01-01', 'YYYY-MM-DD') + (1735948800 / 86400)
FROM DUAL;
-- Output: 04-JAN-25

-- From milliseconds
SELECT TO_DATE('1970-01-01', 'YYYY-MM-DD') + (1735948800000 / 86400000)
FROM DUAL;`}
                  />
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">2</span>
                    Format Date Output
                  </h3>
                  <CodeBlock
                    id="oracle-4"
                    code={`-- Format as string
SELECT TO_CHAR(
  TO_DATE('1970-01-01', 'YYYY-MM-DD') + (1735948800 / 86400),
  'YYYY-MM-DD HH24:MI:SS'
) FROM DUAL;
-- Output: 2025-01-04 12:00:00

SELECT TO_CHAR(
  TO_DATE('1970-01-01', 'YYYY-MM-DD') + (1735948800 / 86400),
  'Mon DD, YYYY at HH12:MI AM'
) FROM DUAL;
-- Output: Jan 04, 2025 at 12:00 PM`}
                  />
                </div>
              </div>
            </div>

            {/* Date Arithmetic */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Oracle: Date Arithmetic</h2>
              <CodeBlock
                id="oracle-date-math"
                code={`-- Calculate difference in days
SELECT (SYSDATE - created_date) AS days_diff
FROM posts;

-- Calculate difference in hours
SELECT (SYSDATE - created_date) * 24 AS hours_diff
FROM posts;

-- Calculate difference in seconds
SELECT (SYSDATE - created_date) * 86400 AS seconds_diff
FROM posts;

-- Add/subtract time
SELECT
  SYSDATE + 7 AS one_week_later,
  SYSDATE - 1/24 AS one_hour_ago,
  SYSDATE + INTERVAL '30' DAY AS thirty_days_later
FROM DUAL;`}
              />
            </div>
          </>
        )}

        {/* SQL Server Content */}
        {activeDb === 'sqlserver' && (
          <>
            {/* DATEDIFF */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">SQL Server: DATEDIFF_BIG() for Unix Timestamp</h2>
              <p className="text-gray-600 mb-6">
                SQL Server 2016+ uses DATEDIFF_BIG() to calculate Unix timestamps
              </p>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">1</span>
                    Get Current Unix Timestamp
                  </h3>
                  <CodeBlock
                    id="sqlserver-1"
                    code={`-- Get current timestamp in seconds (SQL Server 2016+)
SELECT DATEDIFF_BIG(SECOND, '1970-01-01 00:00:00', GETUTCDATE());
-- Output: 1735948800

-- Get milliseconds
SELECT DATEDIFF_BIG(MILLISECOND, '1970-01-01 00:00:00', GETUTCDATE());
-- Output: 1735948800000

-- For SQL Server 2014 and earlier (use DATEDIFF, limited range)
SELECT DATEDIFF(SECOND, '1970-01-01 00:00:00', GETUTCDATE());`}
                  />
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">2</span>
                    Convert Date to Unix Timestamp
                  </h3>
                  <CodeBlock
                    id="sqlserver-2"
                    code={`-- Convert specific datetime
SELECT DATEDIFF_BIG(SECOND, '1970-01-01', '2025-01-04 12:00:00');
-- Output: 1735948800

-- Convert column value
SELECT
  id,
  created_at,
  DATEDIFF_BIG(SECOND, '1970-01-01', created_at) AS unix_timestamp
FROM users;`}
                  />
                </div>
              </div>
            </div>

            {/* DATEADD */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">SQL Server: DATEADD() Convert Unix Timestamp</h2>
              <p className="text-gray-600 mb-6">
                Use DATEADD() to convert Unix timestamp back to datetime
              </p>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">1</span>
                    Convert Timestamp to Datetime
                  </h3>
                  <CodeBlock
                    id="sqlserver-3"
                    code={`-- From seconds
SELECT DATEADD(SECOND, 1735948800, '1970-01-01');
-- Output: 2025-01-04 12:00:00.000

-- From milliseconds
SELECT DATEADD(MILLISECOND, 1735948800000, '1970-01-01');
-- Output: 2025-01-04 12:00:00.000`}
                  />
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">2</span>
                    Format DateTime Output
                  </h3>
                  <CodeBlock
                    id="sqlserver-4"
                    code={`-- Format as string (SQL Server 2012+)
SELECT FORMAT(DATEADD(SECOND, 1735948800, '1970-01-01'), 'yyyy-MM-dd HH:mm:ss');
-- Output: 2025-01-04 12:00:00

SELECT FORMAT(DATEADD(SECOND, 1735948800, '1970-01-01'), 'MMM dd, yyyy at hh:mm tt');
-- Output: Jan 04, 2025 at 12:00 PM

-- Using CONVERT (older versions)
SELECT CONVERT(VARCHAR, DATEADD(SECOND, 1735948800, '1970-01-01'), 120);
-- Output: 2025-01-04 12:00:00`}
                  />
                </div>
              </div>
            </div>

            {/* Date Arithmetic */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">SQL Server: Date Calculations</h2>
              <CodeBlock
                id="sqlserver-diff"
                code={`-- Calculate differences
SELECT
  DATEDIFF(DAY, created_at, GETDATE()) AS days_diff,
  DATEDIFF(HOUR, created_at, GETDATE()) AS hours_diff,
  DATEDIFF(MINUTE, created_at, GETDATE()) AS minutes_diff
FROM posts;

-- Add/subtract time
SELECT
  DATEADD(DAY, 7, GETDATE()) AS one_week_later,
  DATEADD(HOUR, -1, GETDATE()) AS one_hour_ago,
  DATEADD(MONTH, 3, created_at) AS three_months_after
FROM posts;

-- Recent posts (last 24 hours)
SELECT * FROM posts
WHERE DATEDIFF(HOUR, created_at, GETDATE()) < 24;`}
              />
            </div>
          </>
        )}

        {/* Comparison Table */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('sql.comparisonTitle')}</h2>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Operation</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">MySQL</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">PostgreSQL</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Oracle</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">SQL Server</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr>
                  <td className="border border-gray-300 px-4 py-3 font-semibold">Get Current Timestamp</td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">UNIX_TIMESTAMP()</code></td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">EXTRACT(EPOCH FROM NOW())</code></td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">(SYSDATE - DATE'1970-01-01') * 86400</code></td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">DATEDIFF_BIG(S, '1970-01-01', GETUTCDATE())</code></td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 font-semibold">Convert to Timestamp</td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">UNIX_TIMESTAMP(date)</code></td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">EXTRACT(EPOCH FROM date)</code></td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">(date - DATE'1970-01-01') * 86400</code></td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">DATEDIFF_BIG(S, '1970-01-01', date)</code></td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 font-semibold">Convert from Timestamp</td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">FROM_UNIXTIME(ts)</code></td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">TO_TIMESTAMP(ts)</code></td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">DATE'1970-01-01' + (ts / 86400)</code></td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">DATEADD(S, ts, '1970-01-01')</code></td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 font-semibold">Format Output</td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">DATE_FORMAT()</code></td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">TO_CHAR()</code></td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">TO_CHAR()</code></td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">FORMAT() / CONVERT()</code></td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 font-semibold">Date Difference</td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">TIMESTAMPDIFF()</code></td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">date1 - date2</code></td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">date1 - date2</code></td>
                  <td className="border border-gray-300 px-4 py-3"><code className="bg-gray-100 px-2 py-1 rounded">DATEDIFF()</code></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Common Use Cases Across All Databases */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('sql.commonUseCases')}</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-bold text-gray-800 mb-2">{t('sql.useCase1.title')}</h3>
              <p className="text-sm text-gray-600">{t('sql.useCase1.desc')}</p>
            </div>
            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="font-bold text-gray-800 mb-2">{t('sql.useCase2.title')}</h3>
              <p className="text-sm text-gray-600">{t('sql.useCase2.desc')}</p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="font-bold text-gray-800 mb-2">{t('sql.useCase3.title')}</h3>
              <p className="text-sm text-gray-600">{t('sql.useCase3.desc')}</p>
            </div>
            <div className="border-l-4 border-orange-500 pl-4">
              <h3 className="font-bold text-gray-800 mb-2">{t('sql.useCase4.title')}</h3>
              <p className="text-sm text-gray-600">{t('sql.useCase4.desc')}</p>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8 border-2 border-yellow-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            ⚡ {t('common.bestPractices')}
          </h2>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">{t('sql.bestPractice1')}</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">{t('sql.bestPractice2')}</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">{t('sql.bestPractice3')}</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">{t('sql.bestPractice4')}</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">{t('sql.bestPractice5')}</span>
            </li>
          </ul>
        </div>

        {/* Related Tools */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t('common:relatedTools', { defaultValue: 'Related Tools' })}
          </h2>

          <div className="grid md:grid-cols-3 gap-4">
            <a href="/timestamp-converter" className="block p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">Unix Timestamp Converter</h3>
              <p className="text-sm text-gray-600">Convert timestamps online</p>
            </a>
            <a href="/python-timestamp" className="block p-4 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">Python Timestamp Guide</h3>
              <p className="text-sm text-gray-600">datetime, time modules</p>
            </a>
            <a href="/javascript-timestamp" className="block p-4 bg-yellow-50 hover:bg-yellow-100 rounded-lg border border-yellow-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">JavaScript Timestamp</h3>
              <p className="text-sm text-gray-600">Date object guide</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
